<template>
  <video v-if="isVideo" :style="spacerStyle" ref="video" muted="muted">
    <source :src="src$" />
  </video>
  <div
    v-else
    class="cms-img"
    :data-cms-width="cmsWidth"
    :data-cms-height="cmsHeight"
    :style="style$"
  >
    <div class="cms-img-spacer" :src="src$" :style="spacerStyle" :alt="alt$" />
    <!-- <VueFreezeframe class="freezeframe" ref="freezeframe" v-if="isAnimatedGif" :src="src$" /> -->
  </div>
</template>

<script setup lang="ts">
import Debug from "debug";
import { computed, ref } from "vue";
import { VueFreezeframe } from "vue-freezeframe";
import { CmsImage } from "../../services/cms/cms-type-definitions";

const debug = Debug("cs.CmsImg");

const props = withDefaults(
  defineProps<{
    src?: CmsImage | string | undefined;
    width?: string | number | undefined;
    height?: string | number | undefined;
    alt?: string | undefined;
    supportAnimation?: boolean;
  }>(),
  {
    src: undefined,
    width: undefined,
    height: undefined,
    alt: undefined,
    supportAnimation: false,
  },
);

const video = ref<HTMLVideoElement | null>(null);

const src$ = computed(() => {
  if (typeof props.src === "object") {
    return props.src.url;
  }

  return props.src;
});

const isAnimatedGif = computed((): boolean => {
  if (!props.supportAnimation || !src$.value) {
    return false;
  }

  const imageUrl = new URL(src$.value);
  return imageUrl.pathname.toLowerCase().endsWith(".gif");
});

const isVideo = computed((): boolean => {
  return (
    typeof props.src === "string" && (props.src.endsWith(".mp4") || props.src.endsWith(".avi"))
  );
});

const cmsWidth = computed(() => {
  if (typeof props.src === "object" && props.src.dimensions) {
    return props.src.dimensions.width;
  }

  return undefined;
});

const cmsHeight = computed(() => {
  if (typeof props.src === "object" && props.src.dimensions) {
    return props.src.dimensions.height;
  }

  return undefined;
});

const style$ = computed(() => ({
  "background-image": isAnimatedGif.value ? "" : `url("${src$.value}")`,
}));

const spacerStyle = computed(() => ({
  "padding-top": `${(100 * (cmsHeight.value || 0)) / (cmsWidth.value || 1)}%`,
  "max-height": "100%",
}));

const alt$ = computed(() => {
  if (props.alt) {
    return props.alt;
  }

  if (typeof props.src === "object" && props.src.alt) {
    return props.src.alt;
  }

  return "";
});

const animate = (resetOnEnter = false) => {
  if (isVideo.value) {
    if (resetOnEnter && video.value) {
      video.value.currentTime = 0;
    }
    if (video.value) video.value.play();
  }
};

const playAnimation = (areReversing: boolean) => {
  if (isAnimatedGif.value && ref<VueFreezeframe>("freezeframe")) {
    const freezeframe = ref<VueFreezeframe>("freezeframe");
    if (!areReversing && freezeframe.value) {
      debug("Playing animation", freezeframe);
      freezeframe.value.start();
    } else {
      debug("Not playing animation");
    }
  }
};

defineExpose({
  animate,
  playAnimation,
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
.cms-img {
  width: 100%;

  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  margin: 0 auto;
}

.freezeframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
